import _ from 'underscore';
import React, { memo, useCallback, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import cx from 'classnames';

import Icon from 'components/Icon';
import { BodyScrollLockContext } from 'contexts/BodyScrollLockContext';

import { PVIContext } from 'contexts/PVIContext';
import { PVI_NO_STYLES_CLASSNAME } from 'utils/helpers/inclusion';
import styles from './styles.module.scss';

export const setInclusivePanelAttributes = (ref) => {
	const PVIbody = document.querySelector('.pvi-body');
	// eslint-disable-next-line no-restricted-syntax
	for (const attr of PVIbody.attributes) {
		if (attr.name !== 'class') {
			ref.current.setAttribute(attr.name, attr.value);
		}
	}
};

const Modal = ({
	isShowModal,
	onRequestClose,
	children,
	header,
	onHeaderClick,
	isShowBack,
	size,
	theme,
	backButtonText,
	autoClose,
	withCloseButton,
	onAfterClose,
	zIndex,
	shouldCloseOnOverlayClick,
	bodyScrollLock,
	mobilePopupBehavior,
	contentClassName
}) => {
	const modalOverlayRef = useRef(null);
	const [, toggleBodyScrollLock] = useContext(BodyScrollLockContext);
	const timeoutHandle = useRef(null);

	const { panelActive } = useContext(PVIContext);

	const resetTimeoutHandle = useCallback(() => {
		if (timeoutHandle.current) {
			clearTimeout(timeoutHandle.current);
			timeoutHandle.current = null;
		}
	}, [timeoutHandle]);

	useEffect(() => {
		const modalElementRef = modalOverlayRef.current;

		return () => {
			if (isShowModal && modalElementRef) {
				toggleBodyScrollLock(modalElementRef, false);
			}

			resetTimeoutHandle();
		};
	}, [isShowModal, !!modalOverlayRef.current]);

	const onClose = useCallback(() => {
		toggleBodyScrollLock(modalOverlayRef.current, false);

		resetTimeoutHandle();
		onRequestClose();
	}, [onRequestClose, resetTimeoutHandle]);

	const onAfterOpen = useCallback(() => {
		if (autoClose) {
			timeoutHandle.current = setTimeout(onClose, autoClose);
		}

		modalOverlayRef.current.scrollTop = 0;

		if (bodyScrollLock) {
			toggleBodyScrollLock(modalOverlayRef.current, true);
		}

		if (panelActive) {
			setInclusivePanelAttributes(modalOverlayRef);
		}
	}, [onClose, autoClose, bodyScrollLock]);

	const modalCx = cx(
		styles.Modal,
		styles[`Modal_theme_${theme}`],
		panelActive && 'pvi-body',
		{
			[styles.Modal_popup]: mobilePopupBehavior
		}
	);
	const modalContentCx = cx(
		styles.Modal__Content,
		styles[`Modal__Content_size_${size}`],
		contentClassName
	);

	return (
		<ReactModal
			overlayRef={(ref) => {
				modalOverlayRef.current = ref;
			}}
			isOpen={isShowModal}
			onAfterOpen={onAfterOpen}
			onRequestClose={onClose}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
			onAfterClose={onAfterClose}
			className={modalContentCx}
			overlayElement={(props, contentElement) => (
				<div {...props}>{contentElement}</div>
			)}
			overlayClassName={modalCx}
			appElement={document.body}
			closeTimeoutMS={150}
			style={{ overlay: { zIndex } }}
		>
			<div className={styles.Modal__Header}>
				{isShowBack ? (
					// eslint-disable-next-line jsx-a11y/click-events-have-key-events
					<div
						role="button"
						tabIndex={0}
						onClick={onHeaderClick}
						className={styles.Modal__Back}
					>
						<Icon icon="chevronLeft" className={styles.Modal__BackIcon} />{' '}
						{backButtonText}
					</div>
				) : (
					header && <span className={styles.Modal__Title}>{header}</span>
				)}

				{withCloseButton && (
					<button
						type="button"
						className={styles.Modal__CloseButton}
						title="Закрыть"
						onClick={onClose}
						arial-label="Закрыть модальное окно"
					>
						{theme === 'new' ? (
							<Icon
								icon="close-bold"
								className={cx(styles.Modal__CloseIcon, PVI_NO_STYLES_CLASSNAME)}
								useClassName={PVI_NO_STYLES_CLASSNAME}
							/>
						) : (
							<Icon
								icon="close"
								className={cx(styles.Modal__CloseIcon, PVI_NO_STYLES_CLASSNAME)}
								useClassName={PVI_NO_STYLES_CLASSNAME}
							/>
						)}
					</button>
				)}
			</div>
			{children}
		</ReactModal>
	);
};

Modal.propTypes = {
	isShowModal: PropTypes.bool.isRequired,
	onRequestClose: PropTypes.func.isRequired,
	children: PropTypes.node,
	header: PropTypes.string,
	onHeaderClick: PropTypes.func,
	autoClose: PropTypes.number,
	onAfterClose: PropTypes.func,
	isShowBack: PropTypes.bool,
	withCloseButton: PropTypes.bool,
	size: PropTypes.oneOf(['medium']),
	theme: PropTypes.oneOf(['message', 'simple', 'new', 'video']),
	backButtonText: PropTypes.string,
	zIndex: PropTypes.number,
	shouldCloseOnOverlayClick: PropTypes.bool,
	bodyScrollLock: PropTypes.bool,
	mobilePopupBehavior: PropTypes.bool,
	contentClassName: PropTypes.string
};

Modal.defaultProps = {
	children: [],
	onHeaderClick: _.noop,
	autoClose: 0,
	onAfterClose: _.noop,
	isShowBack: false,
	withCloseButton: true,
	backButtonText: 'Назад',
	zIndex: 100,
	shouldCloseOnOverlayClick: true,
	bodyScrollLock: true,
	mobilePopupBehavior: false
};

export default memo(Modal);
